import { useState } from 'react';
import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import AppShell, { SettingsGroup } from '../components/layout/AppShell';
import { useAuth } from '../contexts/AuthContext';
import DesignSystem from '../designSystem';
import { changePassword } from '../services/api';
import { useT } from '../i18n/useT';

export default function ChangePasswordPage() {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user?.token) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await changePassword(current, next, confirm, user.token);
      setSuccess(res.message || res.title || t('changePasswordSuccess'));
      setCurrent('');
      setNext('');
      setConfirm('');
      setTimeout(() => navigate('/settings'), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('changePasswordFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={t('settingsChangePassword')} showBackButton maxWidth="sm" showCompanion={false}>
      <Typography sx={{ mb: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
        {t('changePasswordHint')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <SettingsGroup>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField type="password" label={t('changePasswordCurrent')} fullWidth value={current} onChange={(e) => setCurrent(e.target.value)} />
          <TextField type="password" label={t('changePasswordNew')} fullWidth value={next} onChange={(e) => setNext(e.target.value)} />
          <TextField type="password" label={t('changePasswordConfirm')} fullWidth value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button
            variant="contained"
            disabled={busy || !current || !next || !confirm}
            onClick={() => void submit()}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 999, px: 3, boxShadow: 'none' }}
          >
            {busy ? t('saving') : t('changePasswordUpdate')}
          </Button>
          <Link
            component={RouterLink}
            to="/forgot-password"
            sx={{
              alignSelf: 'flex-start',
              mt: 0.5,
              py: 0.5,
              color: DesignSystem.colors.primary,
              textDecoration: 'none',
              fontSize: DesignSystem.typography.bodySmall.fontSize,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('loginForgotPassword')}
          </Link>
        </Box>
      </SettingsGroup>
    </AppShell>
  );
}
